// The product-master layout for every master screen:
//
//   command bar (title, count, the page's own toolbar icons and extra buttons)
//   ┌ list panel ─────────────────┐ ┌ detail panel ─────────────────┐
//   │ search · clear · New        │ │ the page's add / edit form,   │
//   │ (filters)                   │ │ rendered right here, or a     │
//   │ batched rows, sticky header │ │ "pick a record" placeholder   │
//   │ Showing x of y              │ │                               │
//   └─────────────────────────────┘ └───────────────────────────────┘
//
// Layout only. Every handler, list, column and form comes from the page, so
// saving, deleting, permissions and exports behave exactly as before; the page
// just hands its pieces over instead of laying them out itself.
import React from "react";
import MasterList from "./MasterList";
import { CountChip } from "./masterUi";

const SearchIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <circle cx="7.2" cy="7.2" r="4.6" />
    <path d="M10.6 10.6 14 14" />
  </svg>
);
const ClearIcon = () => (
  <svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M3 3l6 6M9 3l-6 6" />
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 14 14" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
    <path d="M7 2.4v9.2M2.4 7h9.2" />
  </svg>
);
const BoxIcon = () => (
  <svg viewBox="0 0 48 48" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M24 5 42 14v20L24 43 6 34V14z" />
    <path d="M6 14l18 9 18-9M24 23v20" />
  </svg>
);

const MasterScreen = ({
  // command bar
  title,
  subtitle,
  count,
  tools, // the page's <ul className="table-top-head"> (export / print / refresh / collapse)
  actions, // extra command-bar buttons (Branch Transfer, settings, ...)
  // list panel
  searchValue,
  onSearchChange,
  onSearchClear,
  searchPlaceholder = "Search - any part",
  onNew, // omit when the user may not add
  newLabel = "New",
  filters, // optional filter row
  columns,
  dataSource,
  rowKey,
  selectedKey,
  onRowClick,
  // detail panel
  detail, // the open add / edit form, or null
  split = true, // false: the list takes the full width (screens without a form)
  emptyTitle = "Pick a record",
  emptyText,
  children, // popups that stay popups (Bootstrap-driven dialogs)
}) => {
  const hasSearch = typeof onSearchChange === "function";
  return (
    <div className={"ax-master ax-screen" + (split ? "" : " ax-single")}>
      <div className="page-wrapper">
        <div className="content">
          {/* ============ COMMAND BAR ============ */}
          <div className="page-header ax-cmd">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{title}</h4>
                {subtitle ? <h6>{subtitle}</h6> : null}
                <CountChip count={count} />
              </div>
            </div>
            {tools}
            {actions}
          </div>

          <div className="ax-grid">
            {/* ============ LIST ============ */}
            <section className="ax-listpanel">
              {(hasSearch || onNew) && (
                <div className="ax-searchrow">
                  {hasSearch && (
                    <div className="ax-searchwrap">
                      <SearchIcon />
                      <input
                        type="text"
                        className="ax-search"
                        autoComplete="off"
                        placeholder={searchPlaceholder}
                        value={searchValue ?? ""}
                        onChange={onSearchChange}
                      />
                      {searchValue && typeof onSearchClear === "function" ? (
                        <button
                          type="button"
                          className="ax-clearsearch"
                          aria-label="Clear search"
                          onClick={onSearchClear}
                        >
                          <ClearIcon />
                        </button>
                      ) : null}
                    </div>
                  )}
                  {onNew && (
                    <button type="button" className="ax-new" onClick={onNew}>
                      <PlusIcon />
                      {newLabel}
                    </button>
                  )}
                </div>
              )}

              {filters ? <div className="ax-filters">{filters}</div> : null}

              <MasterList
                columns={columns}
                dataSource={dataSource}
                rowKey={rowKey}
                selectedKey={selectedKey}
                onRowClick={onRowClick}
              />
            </section>

            {/* ============ DETAIL ============ */}
            {split && (
              <section className="ax-detailslot">
                {detail || (
                  <div className="ax-dempty">
                    <div className="in">
                      <BoxIcon />
                      <h2>{emptyTitle}</h2>
                      <p>
                        {emptyText ||
                          (onRowClick
                            ? "Select a row on the left to edit it here"
                            : "Use the edit button on a row to open it here") +
                            (onNew ? ", or press New to add one." : ".") +
                            " The list stays put while you work."}
                      </p>
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

export default MasterScreen;
